import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Send, KeyRound, UserCheck, UserX, Shield, Save, UserPlus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import ModalShell from '../ui/ModalShell';
import usePotvrzeni from '../ui/usePotvrzeni';
import Dropdown from '../receptura/Dropdown';
import PristupyStrom, { NIC, slozitPristupy } from '../pristupy/PristupyStrom';
import PozvatModal from '../../pages/PozvatModal';
import { muze } from '../../lib/moduly';
import {
    SECTION_COLOR, CERVENA, nacti, posli, tlumene, cara,
    Nadpis, Pole, RadekPanelu, Tlacitko, PatickaModalu,
} from './ui';

/**
 * Správa → Uživatelé. Norma §1: seznam účtů s pohledy (Aktivní · Admini ·
 * K dořešení · Neaktivní · Šablony přístupů) · detail účtu nebo šablony ve
 * sloupci (úprava přímo v detailu, Uložit v hlavičce — jako karta v Lidech)
 * · třetí sloupec `panelVzdy` s počty a „K dořešení" (bez PINu, pozvánka
 * vypršela, dlouho nepřihlášen). Nový účet, PIN a zaměstnanec = ModalShell,
 * deaktivace a smazání šablony = usePotvrzeni.
 *
 * Dřív: Nastavení → Uživatelé — tabulka s formulářem nad ní a druhá
 * záložka Šablony přístupů; nativní selecty, window.confirm.
 */
const ROLE = { superadmin: 'Superadmin', admin: 'Admin', uzivatel: 'Uživatel' };
const NEPRIHLASEN_DNI = 60;
const dniOd = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 864e5) : null);
const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('cs-CZ') : '—');

export function kDoreseniUctu(uzivatele = []) {
    const out = [];
    for (const u of uzivatele) {
        if (u.stav !== 'aktivni') continue;
        const jmeno = u.cele_jmeno || `${u.jmeno} ${u.prijmeni}`;
        const pozvankaVyprsela = !u.last_login_at && u.pin_platnost_do && new Date(u.pin_platnost_do) < new Date();
        if (pozvankaVyprsela) out.push({ id: `poz-${u.id}`, uzivatelId: u.id, problem: true, titulek: `${jmeno} · pozvánka vypršela`, popis: 'Vygeneruj nový PIN nebo pošli pozvánku znovu.' });
        else if (!u.ma_pin) out.push({ id: `pin-${u.id}`, uzivatelId: u.id, problem: true, titulek: `${jmeno} · bez PINu`, popis: 'Nemůže se přihlásit.' });
        const dni = dniOd(u.last_login_at);
        if (u.ma_pin && dni != null && dni >= NEPRIHLASEN_DNI) out.push({ id: `stary-${u.id}`, uzivatelId: u.id, problem: false, titulek: `${jmeno} · ${dni} dní nepřihlášen`, popis: 'Zvaž deaktivaci účtu.' });
    }
    return out.sort((a, b) => Number(b.problem) - Number(a.problem));
}

const POHLEDY = [
    { id: 'aktivni', label: 'Aktivní', patri: (u) => u.stav === 'aktivni' },
    { id: 'admini', label: 'Admini', patri: (u) => u.stav === 'aktivni' && u.role !== 'uzivatel' },
    { id: 'kdoreseni', label: 'K dořešení', upozorneni: true, patri: (u, kd) => kd.has(u.id) },
    { id: 'neaktivni', label: 'Neaktivní', patri: (u) => u.stav !== 'aktivni' },
    { id: 'sablony', label: 'Šablony přístupů' },
];

export default function SpravaUzivatele({ user, isDark, isMobile, cardStyle, inputStyle, rezervaDole }) {
    const upravuje = muze(user, 'sprava.uzivatele', 'upravuje');
    const [uzivatele, setUzivatele] = useState([]);
    const [profily, setProfily] = useState([]);
    const [katalog, setKatalog] = useState([]);
    const [nacteno, setNacteno] = useState(false);
    const [pohled, setPohled] = useState('aktivni');
    const [hledani, setHledani] = useState('');
    const [vybrany, setVybrany] = useState(null);   // { druh: 'ucet'|'sablona', id }
    const [modal, setModal] = useState(null);       // 'novy' | 'pozvat' | 'novaSablona' | { pin } | { zamestnanecPro }
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    const obnov = useCallback(() => Promise.all([
        nacti('/api/uzivatele').then(setUzivatele),
        nacti('/api/profily').then(setProfily).catch(() => setProfily([])),
        nacti('/api/profily/moduly').then(setKatalog).catch(() => setKatalog([])),
    ]).finally(() => setNacteno(true)), []);
    useEffect(() => { obnov(); }, [obnov]);

    const kDoreseni = useMemo(() => kDoreseniUctu(uzivatele), [uzivatele]);
    const sKD = useMemo(() => new Set(kDoreseni.map((k) => k.uzivatelId)), [kDoreseni]);
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map((p) => [p.id, p.id === 'sablony' ? profily.length : uzivatele.filter((u) => p.patri(u, sKD)).length])), [uzivatele, profily, sKD]);
    const q = hledani.trim().toLowerCase();
    const ml = tlumene(isDark);
    const vybranyUcet = vybrany?.druh === 'ucet' ? uzivatele.find((u) => u.id === vybrany.id) : null;
    const vybranaSablona = vybrany?.druh === 'sablona' ? profily.find((p) => p.id === vybrany.id) : null;

    const radekStyl = (sel) => ({
        gap: 12, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: 56, cursor: 'pointer', border: 'none',
        borderBottom: `1px solid ${cara(isDark)}`, background: sel ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)') : 'transparent',
        boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
    });
    const inicialy = (u) => `${(u.jmeno || '?')[0]}${(u.prijmeni || '')[0] || ''}`.toUpperCase();

    const seznam = ({ siroky }) => {
        const jeSablony = pohled === 'sablony';
        const akt = POHLEDY.find((p) => p.id === pohled);
        const ucty = jeSablony ? [] : uzivatele.filter((u) => akt.patri(u, sKD) && (!q || `${u.jmeno} ${u.prijmeni} ${u.email} ${u.profil?.nazev || ''}`.toLowerCase().includes(q)));
        const sablony = jeSablony ? profily.filter((p) => !q || p.nazev.toLowerCase().includes(q)) : [];
        const akce = isMobile || !upravuje ? [] : jeSablony
            ? [{ id: 'novaSablona', label: 'Nová šablona', Icon: Plus, primarni: true, onClick: () => setModal('novaSablona') }]
            : [
                { id: 'novy', label: 'Nový uživatel', Icon: Plus, primarni: true, onClick: () => setModal('novy') },
                { id: 'pozvat', label: 'Pozvat zaměstnance', Icon: Send, onClick: () => setModal('pozvat') },
            ];
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={ml} inputStyle={inputStyle} barva={SECTION_COLOR}
                    hledani={{ value: hledani, onChange: setHledani, placeholder: jeSablony ? 'Hledat šablonu…' : 'Hledat jméno, e-mail, šablonu…', placeholderKratky: 'Hledat' }}
                    akce={akce}
                    pohled={POHLEDY.map((p) => (
                        <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={pohled === p.id} upozorneni={p.upozorneni && pocty[p.id] > 0}
                            onClick={() => { setPohled(p.id); setVybrany(null); }} isDark={isDark} mutedColor={ml} barva={SECTION_COLOR} kompaktni={isMobile} />
                    ))}
                    pod={isMobile && kDoreseni.length > 0 ? <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} styl={{ marginTop: 8 }}
                        renderAkce={(k) => <button type="button" onClick={() => { setPohled('aktivni'); setVybrany({ druh: 'ucet', id: k.uzivatelId }); }} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>} /> : null} />
                {siroky && !jeSablony && (
                    <div className="flex items-center" style={{ gap: 12, padding: '8px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: ml, borderBottom: `1px solid ${cara(isDark)}` }}>
                        <span style={{ width: 30 }} />
                        <span className="flex-1">Uživatel</span>
                        <span style={{ width: 100 }}>Role</span>
                        <span style={{ width: 160 }}>Šablona</span>
                        <span style={{ width: 110, textAlign: 'right' }}>Přihlášen</span>
                    </div>
                )}
                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                    {!nacteno && <p className="text-sm" style={{ color: ml, padding: 16 }}>Načítám…</p>}
                    {nacteno && !jeSablony && ucty.length === 0 && <p className="text-center text-sm" style={{ color: ml, padding: '32px 16px' }}>{q ? 'Nic se neshoduje.' : 'V tomhle pohledu nikdo není.'}</p>}
                    {ucty.map((u) => {
                        const sel = vybrany?.druh === 'ucet' && vybrany.id === u.id;
                        const problem = kDoreseni.some((k) => k.uzivatelId === u.id && k.problem);
                        return (
                            <button key={u.id} type="button" onClick={() => setVybrany({ druh: 'ucet', id: u.id })} className="w-full text-left flex items-center" style={radekStyl(sel)}>
                                <span className="flex items-center justify-center shrink-0" style={{ width: 30, height: 30, borderRadius: 15, fontSize: 11, fontWeight: 700, color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}55` }}>{inicialy(u)}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="truncate font-semibold" style={{ margin: 0, color: u.stav === 'aktivni' ? 'var(--text-primary)' : ml, fontSize: isMobile ? 15 : 13.5 }}>{u.cele_jmeno || `${u.jmeno} ${u.prijmeni}`}</p>
                                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: ml }}>{siroky ? u.email : [ROLE[u.role], u.profil?.nazev].filter(Boolean).join(' · ')}</p>
                                </div>
                                {siroky && <span style={{ width: 100, fontSize: 12.5, color: 'var(--text-primary)' }}>{ROLE[u.role] || u.role}</span>}
                                {siroky && <span className="truncate" style={{ width: 160, fontSize: 12.5, color: u.profil ? 'var(--text-primary)' : ml }}>{u.profil?.nazev || '—'}{u.pocet_vyjimek ? <span style={{ color: ml }}> +{u.pocet_vyjimek}</span> : null}</span>}
                                {siroky && <span style={{ width: 110, textAlign: 'right', fontSize: 12.5, color: ml, fontVariantNumeric: 'tabular-nums' }}>{datum(u.last_login_at)}</span>}
                                {problem && <span aria-label="K dořešení" style={{ width: 8, height: 8, borderRadius: 4, background: CERVENA, flexShrink: 0 }} />}
                            </button>
                        );
                    })}
                    {sablony.map((p) => {
                        const sel = vybrany?.druh === 'sablona' && vybrany.id === p.id;
                        const pocet = uzivatele.filter((u) => u.profil_id === p.id && u.stav === 'aktivni').length;
                        const klicu = Object.keys(Array.isArray(p.opravneni) ? {} : (p.opravneni || {})).length;
                        return (
                            <button key={p.id} type="button" onClick={() => setVybrany({ druh: 'sablona', id: p.id })} className="w-full text-left flex items-center" style={radekStyl(sel)}>
                                <Shield size={16} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                                <div className="flex-1 min-w-0">
                                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{p.nazev}</p>
                                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: ml }}>{pocet} {pocet === 1 ? 'uživatel' : pocet >= 2 && pocet <= 4 ? 'uživatelé' : 'uživatelů'} · {klicu} přístupů</p>
                                </div>
                            </button>
                        );
                    })}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            </div>
        );
    };

    const detail = () => {
        if (vybranyUcet) {
            return <DetailUctu key={vybranyUcet.id} u={vybranyUcet} profily={profily} katalog={katalog} upravuje={upravuje} isDark={isDark} isMobile={isMobile}
                cardStyle={cardStyle} inputStyle={inputStyle} rezervaDole={rezervaDole} potvrdit={potvrdit}
                onZpet={() => setVybrany(null)} onObnov={obnov} onPin={(pin) => setModal({ pin, jmeno: vybranyUcet.cele_jmeno })}
                onZamestnanec={() => setModal({ zamestnanecPro: vybranyUcet })} />;
        }
        if (vybranaSablona) {
            return <DetailSablony key={vybranaSablona.id} p={vybranaSablona} uzivatele={uzivatele} katalog={katalog} upravuje={upravuje} isDark={isDark} isMobile={isMobile}
                cardStyle={cardStyle} inputStyle={inputStyle} rezervaDole={rezervaDole} potvrdit={potvrdit}
                onZpet={() => setVybrany(null)} onObnov={obnov} onSmazano={() => { setVybrany(null); obnov(); }} />;
        }
        return null;
    };

    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
            <Nadpis isDark={isDark}>Účty</Nadpis>
            <RadekPanelu isDark={isDark} popis="Aktivní" hodnota={pocty.aktivni} />
            <RadekPanelu isDark={isDark} popis="Z toho admini" hodnota={pocty.admini} />
            <RadekPanelu isDark={isDark} popis="Přihlášen za 7 dní" hodnota={uzivatele.filter((u) => u.stav === 'aktivni' && dniOd(u.last_login_at) != null && dniOd(u.last_login_at) < 7).length} />
            <RadekPanelu isDark={isDark} popis="Neaktivní" hodnota={pocty.neaktivni} />
            <RadekPanelu isDark={isDark} popis="Šablon přístupů" hodnota={profily.length} />
            <Nadpis isDark={isDark} mt={16}>K dořešení</Nadpis>
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne={nacteno ? 'U účtů je všechno v pořádku.' : 'Načítám…'}
                renderAkce={(k) => <button type="button" onClick={() => { setPohled('aktivni'); setVybrany({ druh: 'ucet', id: k.uzivatelId }); }} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>} />
        </div>
    );

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!(vybranyUcet || vybranaSablona)} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznam} detail={detail} panel={panel} />
            {modal === 'novy' && <NovyUcet profily={profily} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle} onClose={() => setModal(null)}
                onUlozeno={(u) => { setModal(null); obnov().then(() => u?.id && setVybrany({ druh: 'ucet', id: u.id })); }} />}
            {modal === 'pozvat' && <PozvatModal isDark={isDark} onClose={() => setModal(null)} onCreated={obnov} />}
            {modal === 'novaSablona' && <NovaSablona isDark={isDark} isMobile={isMobile} inputStyle={inputStyle} onClose={() => setModal(null)}
                onUlozeno={(p) => { setModal(null); setPohled('sablony'); obnov().then(() => p?.id && setVybrany({ druh: 'sablona', id: p.id })); }} />}
            {modal?.pin && (
                <ModalShell onClose={() => setModal(null)} isDark={isDark} nadpis="Nový PIN" ikona={KeyRound} barva={SECTION_COLOR}
                    paticka={<div className="flex justify-end"><Tlacitko isDark={isDark} plna={isMobile ? 'cela' : true} onClick={() => setModal(null)}>Hotovo</Tlacitko></div>}>
                    <div style={{ padding: 16, textAlign: 'center' }}>
                        <p className="text-sm" style={{ color: ml, margin: 0 }}>{modal.jmeno}</p>
                        <p style={{ fontSize: 36, fontWeight: 800, letterSpacing: '0.3em', margin: '10px 0', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{modal.pin}</p>
                        <p className="text-xs" style={{ color: ml, margin: 0 }}>Předej ho osobně. Při prvním přihlášení si ho změní.</p>
                    </div>
                </ModalShell>
            )}
            {modal?.zamestnanecPro && <VytvoritZamestnance u={modal.zamestnanecPro} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle}
                onClose={() => setModal(null)} onUlozeno={() => { setModal(null); obnov(); }} />}
            {potvrzeni}
        </div>
    );
}

function HlavickaDetailu({ titulek, podtitulek, onZpet, isDark, isMobile, vpravo = null, popisZpet }) {
    return (
        <div style={{ padding: isMobile ? '6px 14px 10px' : '16px 18px 12px', borderBottom: `1px solid ${cara(isDark)}`, flexShrink: 0 }}>
            {isMobile && <ZpetTlacitko onClick={onZpet} popis={popisZpet} sklo isDark={isDark} style={{ marginBottom: 8 }} />}
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <h2 className="font-bold truncate" style={{ fontSize: 18, margin: 0, color: 'var(--text-primary)' }}>{titulek}</h2>
                    <p className="text-xs truncate" style={{ color: tlumene(isDark), margin: '4px 0 0' }}>{podtitulek}</p>
                </div>
                {vpravo}
            </div>
        </div>
    );
}

function DetailUctu({ u, profily, katalog, upravuje, isDark, isMobile, cardStyle, inputStyle, rezervaDole, potvrdit, onZpet, onObnov, onPin, onZamestnanec }) {
    const [f, setF] = useState({ jmeno: u.jmeno || '', prijmeni: u.prijmeni || '', email: u.email || '', role: u.role || 'uzivatel', profil_id: u.profil_id ? String(u.profil_id) : '' });
    const [vyjimky, setVyjimky] = useState(null);
    const [vyjimkyPuvodne, setVyjimkyPuvodne] = useState(null);
    const [strom, setStrom] = useState(false);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const ml = tlumene(isDark);

    useEffect(() => {
        nacti(`/api/uzivatele/${u.id}/pristupy`).then((d) => {
            const v = d && !Array.isArray(d.vyjimky) ? (d.vyjimky || {}) : {};
            setVyjimky(v); setVyjimkyPuvodne(v);
        }).catch(() => { setVyjimky({}); setVyjimkyPuvodne({}); });
    }, [u.id]);

    const sablona = profily.find((p) => String(p.id) === f.profil_id)?.opravneni;
    const sablonaMapa = sablona && !Array.isArray(sablona) ? sablona : {};
    const zmenenoUcet = f.jmeno !== (u.jmeno || '') || f.prijmeni !== (u.prijmeni || '') || f.email !== (u.email || '') || f.role !== u.role || f.profil_id !== (u.profil_id ? String(u.profil_id) : '');
    const zmenenoPristupy = vyjimky && vyjimkyPuvodne && JSON.stringify(vyjimky) !== JSON.stringify(vyjimkyPuvodne);
    const zmeneno = zmenenoUcet || zmenenoPristupy;
    const pocetUprav = vyjimky ? Object.entries(vyjimky).filter(([k, v]) => v !== (sablonaMapa[k] ?? NIC)).length : 0;

    const zmenPristupy = (klice, uroven) => setVyjimky((prev) => {
        const dalsi = { ...(prev || {}) };
        for (const k of klice) { if (uroven === (sablonaMapa[k] ?? NIC)) delete dalsi[k]; else dalsi[k] = uroven; }
        return dalsi;
    });
    const vratit = (klice) => setVyjimky((prev) => { const d = { ...(prev || {}) }; for (const k of klice) delete d[k]; return d; });

    const uloz = async () => {
        setUkladam(true); setChyba(null);
        try {
            if (zmenenoUcet) await posli(`/api/uzivatele/${u.id}`, 'PUT', { ...f, profil_id: f.profil_id || null, stav: u.stav });
            if (f.role !== 'superadmin' && zmenenoPristupy) await posli(`/api/uzivatele/${u.id}/pristupy`, 'PUT', { vyjimky });
            setVyjimkyPuvodne(vyjimky);
            await onObnov();
        } catch (e) { setChyba(e.message); }
        setUkladam(false);
    };
    const prepniStav = async () => {
        const deaktivovat = u.stav === 'aktivni';
        if (!(await potvrdit(deaktivovat ? `Deaktivovat účet ${u.cele_jmeno}?` : `Obnovit účet ${u.cele_jmeno}?`, {
            text: deaktivovat ? 'Nepůjde se přihlásit. Data a historie zůstanou.' : null, potvrdit: deaktivovat ? 'Deaktivovat' : 'Obnovit', nebezpecne: deaktivovat,
        }))) return;
        try { await posli(`/api/uzivatele/${u.id}`, 'PUT', { jmeno: u.jmeno, prijmeni: u.prijmeni, email: u.email, role: u.role, profil_id: u.profil_id, stav: deaktivovat ? 'neaktivni' : 'aktivni' }); await onObnov(); }
        catch (e) { setChyba(e.message); }
    };
    const generujPin = async () => {
        if (!(await potvrdit(`Vygenerovat nový PIN pro ${u.cele_jmeno}?`, { text: 'Starý PIN přestane platit.', potvrdit: 'Vygenerovat', nebezpecne: false }))) return;
        try { const d = await posli(`/api/uzivatele/${u.id}/generuj-pin`, 'POST'); onPin(d.pin); await onObnov(); }
        catch (e) { setChyba(e.message); }
    };

    const pole = (k, popisek, props = {}) => (
        <label style={{ display: 'block', marginBottom: 10 }}>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{popisek}</span>
            <input value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} disabled={!upravuje} style={inputStyle} {...props} />
        </label>
    );

    return (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <HlavickaDetailu titulek={u.cele_jmeno || `${u.jmeno} ${u.prijmeni}`} podtitulek={[ROLE[u.role], u.profil?.nazev, u.stav !== 'aktivni' ? 'neaktivní' : null].filter(Boolean).join(' · ')}
                onZpet={onZpet} popisZpet="Zpět na uživatele" isDark={isDark} isMobile={isMobile}
                vpravo={upravuje && zmeneno ? <Tlacitko isDark={isDark} Icon={Save} plna onClick={uloz} disabled={ukladam}>{ukladam ? 'Ukládám…' : 'Uložit'}</Tlacitko> : null} />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '12px 16px' : '14px 18px' }}>
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '0 0 10px' }}>{chyba}</p>}
                <Nadpis isDark={isDark}>Účet</Nadpis>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap: 12 }}>
                    {pole('jmeno', 'Jméno')}
                    {pole('prijmeni', 'Příjmení')}
                </div>
                {pole('email', 'E-mail', { type: 'email' })}

                <Nadpis isDark={isDark} mt={14}>Přístup</Nadpis>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                    <div>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Role</span>
                        {upravuje ? (
                            <Dropdown value={f.role} isDark={isDark} accent={SECTION_COLOR} onChange={(role) => setF((p) => ({ ...p, role }))}
                                options={[{ value: 'uzivatel', label: 'Uživatel' }, { value: 'admin', label: 'Admin' }, { value: 'superadmin', label: 'Superadmin' }]} />
                        ) : <Pole popisek="" isDark={isDark}>{ROLE[f.role]}</Pole>}
                    </div>
                    <div>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Šablona přístupů</span>
                        {upravuje ? (
                            <Dropdown value={f.profil_id} isDark={isDark} accent={SECTION_COLOR} onChange={(v) => setF((p) => ({ ...p, profil_id: v || '' }))}
                                options={[{ value: '', label: '— Bez šablony —' }, ...profily.map((p) => ({ value: String(p.id), label: p.nazev }))]} />
                        ) : <Pole popisek="" isDark={isDark}>{u.profil?.nazev}</Pole>}
                    </div>
                </div>
                {f.role === 'superadmin' ? (
                    <p className="text-xs" style={{ color: ml, margin: '10px 0 0' }}>Superadmin má vždy všechny přístupy.</p>
                ) : (
                    <div style={{ marginTop: 10 }}>
                        <button type="button" onClick={() => setStrom((s) => !s)} className="flex items-center gap-1.5 text-sm font-semibold"
                            style={{ background: 'none', border: 'none', padding: '8px 0', minHeight: 44, cursor: 'pointer', color: 'var(--text-primary)' }}>
                            {strom ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Přístupy k modulům
                            <span style={{ fontWeight: 400, color: ml }}>{pocetUprav ? `· ${pocetUprav} ${pocetUprav === 1 ? 'výjimka' : pocetUprav < 5 ? 'výjimky' : 'výjimek'} ze šablony` : '· podle šablony'}</span>
                        </button>
                        {strom && vyjimky && (
                            <PristupyStrom katalog={katalog} isDark={isDark} disabled={!upravuje}
                                hodnoty={slozitPristupy(sablonaMapa, vyjimky)} sablona={sablonaMapa} onZmena={zmenPristupy} onVratit={vratit} />
                        )}
                    </div>
                )}

                <Nadpis isDark={isDark} mt={18}>Přihlášení</Nadpis>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, minmax(0, 1fr))', columnGap: 12 }}>
                    <Pole popisek="PIN" isDark={isDark}>{u.ma_pin ? (u.pin_zmeneno ? 'nastaven' : 'první PIN (nezměněn)') : <span style={{ color: CERVENA }}>chybí</span>}</Pole>
                    <Pole popisek="Naposledy přihlášen" isDark={isDark}>{u.last_login_at ? datum(u.last_login_at) : null}</Pole>
                    <Pole popisek="Pozvánka" isDark={isDark}>{u.pozvanka_odeslano_at ? datum(u.pozvanka_odeslano_at) : null}</Pole>
                </div>
                {upravuje && <div style={{ marginTop: 4 }}><Tlacitko isDark={isDark} Icon={KeyRound} onClick={generujPin}>Vygenerovat PIN</Tlacitko></div>}

                <Nadpis isDark={isDark} mt={18}>Zaměstnanec</Nadpis>
                {u.zamestnanec ? (
                    <Pole popisek="Karta v Lidech" isDark={isDark}>
                        {[u.zamestnanec.jmeno, u.zamestnanec.prijmeni].filter(Boolean).join(' ') || u.cele_jmeno}
                        {u.zamestnanec.typ_uvazku?.nazev && <span style={{ color: ml }}> · {u.zamestnanec.typ_uvazku.nazev}</span>}
                    </Pole>
                ) : (
                    <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                        <p className="text-sm" style={{ color: ml, margin: 0 }}>Účet nemá kartu zaměstnance.</p>
                        {upravuje && <Tlacitko isDark={isDark} Icon={UserPlus} onClick={onZamestnanec}>Vytvořit zaměstnance</Tlacitko>}
                    </div>
                )}

                {upravuje && !isMobile && (
                    <div style={{ marginTop: 24, paddingTop: 14, borderTop: `1px solid ${cara(isDark)}` }}>
                        <Tlacitko isDark={isDark} Icon={u.stav === 'aktivni' ? UserX : UserCheck} onClick={prepniStav}>{u.stav === 'aktivni' ? 'Deaktivovat účet' : 'Obnovit účet'}</Tlacitko>
                    </div>
                )}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );
}

function DetailSablony({ p, uzivatele, katalog, upravuje, isDark, isMobile, cardStyle, inputStyle, rezervaDole, potvrdit, onZpet, onObnov, onSmazano }) {
    const puvodni = p.opravneni && !Array.isArray(p.opravneni) ? p.opravneni : {};
    const [nazev, setNazev] = useState(p.nazev);
    const [opravneni, setOpravneni] = useState(puvodni);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const lide = uzivatele.filter((u) => u.profil_id === p.id);
    const zmeneno = nazev !== p.nazev || JSON.stringify(opravneni) !== JSON.stringify(puvodni);

    const zmen = (klice, uroven) => setOpravneni((prev) => {
        const d = { ...prev };
        for (const k of klice) { if (uroven === NIC) delete d[k]; else d[k] = uroven; }
        return d;
    });
    const uloz = async () => {
        setUkladam(true); setChyba(null);
        try { await posli(`/api/profily/${p.id}`, 'PUT', { nazev, opravneni }); await onObnov(); }
        catch (e) { setChyba(e.message); }
        setUkladam(false);
    };
    const smaz = async () => {
        if (!(await potvrdit(`Smazat šablonu „${p.nazev}"?`, { text: lide.length ? `Používá ji ${lide.length} účtů — ty zůstanou bez šablony.` : null, potvrdit: 'Smazat' }))) return;
        try { await posli(`/api/profily/${p.id}`, 'DELETE'); onSmazano(); } catch (e) { setChyba(e.message); }
    };

    return (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <HlavickaDetailu titulek={p.nazev} podtitulek={`Šablona přístupů · ${lide.length} ${lide.length === 1 ? 'účet' : lide.length >= 2 && lide.length <= 4 ? 'účty' : 'účtů'}`}
                onZpet={onZpet} popisZpet="Zpět na šablony" isDark={isDark} isMobile={isMobile}
                vpravo={upravuje && zmeneno ? <Tlacitko isDark={isDark} Icon={Save} plna onClick={uloz} disabled={ukladam}>{ukladam ? 'Ukládám…' : 'Uložit'}</Tlacitko> : null} />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '12px 16px' : '14px 18px' }}>
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '0 0 10px' }}>{chyba}</p>}
                <label style={{ display: 'block', marginBottom: 14, maxWidth: 420 }}>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Název</span>
                    <input value={nazev} onChange={(e) => setNazev(e.target.value)} disabled={!upravuje} style={inputStyle} />
                </label>
                <Nadpis isDark={isDark}>Přístupy</Nadpis>
                <PristupyStrom katalog={katalog} isDark={isDark} disabled={!upravuje} hodnoty={opravneni} onZmena={zmen} />
                {lide.length > 0 && (
                    <>
                        <Nadpis isDark={isDark} mt={18}>Kdo ji má</Nadpis>
                        <p className="text-sm" style={{ color: 'var(--text-primary)', margin: 0 }}>{lide.map((u) => u.cele_jmeno).join(', ')}</p>
                    </>
                )}
                {upravuje && !isMobile && (
                    <div style={{ marginTop: 24, paddingTop: 14, borderTop: `1px solid ${cara(isDark)}` }}>
                        <Tlacitko isDark={isDark} Icon={Trash2} onClick={smaz}>Smazat šablonu</Tlacitko>
                    </div>
                )}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );
}

function NovyUcet({ profily, isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [f, setF] = useState({ jmeno: '', prijmeni: '', email: '', role: 'uzivatel', profil_id: '', vytvorit_zamestnance: false });
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const uloz = async () => {
        if (!f.jmeno.trim() || !f.prijmeni.trim() || !f.email.trim()) { setChyba('Vyplň jméno, příjmení a e-mail.'); return; }
        setUkladam(true); setChyba(null);
        try { onUlozeno(await posli('/api/uzivatele', 'POST', { ...f, profil_id: f.profil_id || null })); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    const pole = (k, popisek, props = {}) => (
        <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{popisek}</span>
            <input value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} style={inputStyle} {...props} />
        </label>
    );
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Nový uživatel" ikona={UserPlus} barva={SECTION_COLOR} sirka={520}
            paticka={<PatickaModalu onZrusit={onClose} onPotvrdit={uloz} ukladam={ukladam} potvrdit="Vytvořit" isDark={isDark} isMobile={isMobile} />}>
            <div style={{ padding: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap: 12 }}>
                    {pole('jmeno', 'Jméno', { autoFocus: true })}
                    {pole('prijmeni', 'Příjmení')}
                </div>
                {pole('email', 'E-mail', { type: 'email' })}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                    <div>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Role</span>
                        <Dropdown value={f.role} isDark={isDark} accent={SECTION_COLOR} onChange={(role) => setF((p) => ({ ...p, role }))}
                            options={[{ value: 'uzivatel', label: 'Uživatel' }, { value: 'admin', label: 'Admin' }, { value: 'superadmin', label: 'Superadmin' }]} />
                    </div>
                    <div>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Šablona přístupů</span>
                        <Dropdown value={f.profil_id} isDark={isDark} accent={SECTION_COLOR} onChange={(v) => setF((p) => ({ ...p, profil_id: v || '' }))}
                            options={[{ value: '', label: '— Bez šablony —' }, ...profily.map((p) => ({ value: String(p.id), label: p.nazev }))]} />
                    </div>
                </div>
                <p className="text-xs" style={{ color: tlumene(isDark), margin: '12px 0 0' }}>Kartu zaměstnance doplníš v detailu účtu. Nového zaměstnance i s PINem e-mailem rychleji založí „Pozvat zaměstnance".</p>
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}

function NovaSablona({ isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [nazev, setNazev] = useState('');
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const kod = nazev.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const uloz = async () => {
        if (!nazev.trim()) { setChyba('Vyplň název.'); return; }
        setUkladam(true); setChyba(null);
        try { onUlozeno(await posli('/api/profily', 'POST', { nazev: nazev.trim(), kod, opravneni: {} })); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Nová šablona přístupů" ikona={Shield} barva={SECTION_COLOR}
            paticka={<PatickaModalu onZrusit={onClose} onPotvrdit={uloz} ukladam={ukladam} potvrdit="Vytvořit" isDark={isDark} isMobile={isMobile} />}>
            <div style={{ padding: 16 }}>
                <label style={{ display: 'block' }}>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Název</span>
                    <input value={nazev} onChange={(e) => setNazev(e.target.value)} style={inputStyle} autoFocus placeholder="např. Vedoucí směny" />
                </label>
                <p className="text-xs" style={{ color: tlumene(isDark), margin: '8px 0 0' }}>Přístupy nastavíš v detailu šablony.</p>
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}

function VytvoritZamestnance({ u, isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [f, setF] = useState({ nadrizeny_id: '', typ_uvazku_id: '', datum_nastupu: '', pozice_id: '' });
    const [ciselniky, setCiselniky] = useState({ zamestnanci: [], uvazky: [], pozice: [] });
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    useEffect(() => {
        Promise.all([nacti('/api/zamestnanci'), nacti('/api/ciselniky/typy-uvazku'), nacti('/api/ciselniky/pozice')])
            .then(([zamestnanci, uvazky, pozice]) => setCiselniky({ zamestnanci, uvazky, pozice })).catch(() => {});
    }, []);
    const uloz = async () => {
        setUkladam(true); setChyba(null);
        try { await posli(`/api/uzivatele/${u.id}/vytvor-zamestnance`, 'POST', f); onUlozeno(); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    const vyber = (k, popisek, options) => (
        <div style={{ marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{popisek}</span>
            <Dropdown value={f[k]} isDark={isDark} accent={SECTION_COLOR} onChange={(v) => setF((p) => ({ ...p, [k]: v || '' }))} options={[{ value: '', label: '—' }, ...options]} />
        </div>
    );
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={`Zaměstnanec pro ${u.cele_jmeno}`} ikona={UserPlus} barva={SECTION_COLOR} sirka={520}
            paticka={<PatickaModalu onZrusit={onClose} onPotvrdit={uloz} ukladam={ukladam} potvrdit="Vytvořit" isDark={isDark} isMobile={isMobile} />}>
            <div style={{ padding: 16 }}>
                {vyber('nadrizeny_id', 'Nadřízený', (ciselniky.zamestnanci || []).map((z) => ({ value: String(z.id), label: z.cele_jmeno || `${z.jmeno} ${z.prijmeni}` })))}
                {vyber('typ_uvazku_id', 'Typ úvazku', (ciselniky.uvazky || []).map((t) => ({ value: String(t.id), label: t.nazev })))}
                {vyber('pozice_id', 'Pozice', (ciselniky.pozice || []).map((p) => ({ value: String(p.id), label: p.nazev })))}
                <label style={{ display: 'block' }}>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Datum nástupu</span>
                    <input type="date" value={f.datum_nastupu} onChange={(e) => setF((p) => ({ ...p, datum_nastupu: e.target.value }))} style={inputStyle} />
                </label>
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}
